<div class="risk-inputs">

  <fieldset class="fieldset">
    <legend class="fieldset-title">Price Range</legend>

    <!-- CR-20260831-004: .param-changed = this control no longer matches what the tab's last run
         sent — toggles paint their selected segment yellow, value inputs get the yellow
         background. Cleared by re-running, reverting, or New Conversation. -->
    <!-- [selectedItems] paired with the keys (CR-20260831-004 defect round 2): on an items
         rebuild ButtonGroup re-feeds its stale self-synced selection objects; supplying the
         CURRENT selected object keeps the mutually-exclusive highlight single and present. -->
    <dx-button-group
      [class.param-changed]="this.isChanged('priceRange.kind')"
      [items]="this.priceButtons"
      keyExpr="id"
      selectionMode="single"
      stylingMode="contained"
      [selectedItemKeys]="[this.priceKind]"
      [selectedItems]="this.priceSelectedItems"
      (onItemClick)="this.onPriceKind($event)"
    ></dx-button-group>

    <!-- Shown for BOTH Range and Fill Gap (showStrikePair): the two forms take the same strike pair
         and differ only in what the engine may do outside it. One block, so the controls cannot
         drift apart per form. -->
    <div class="field" *ngIf="this.showStrikePair">
      <div class="label">Start / End Strike</div>
      <div class="pair">
        <ets-strikes-dropdown [targetInstrument]="this.targetInstrument" [expiration]="this.expiration"
                              [class.param-changed]="this.isChanged('startStrike')"
                              [(strike)]="this.startStrike" [showClearButton]="true"
                              [disable]="this.strikePairDisabled"
                              (strikeChanged)="this.emit()"></ets-strikes-dropdown>
        <ets-strikes-dropdown [targetInstrument]="this.targetInstrument" [expiration]="this.expiration"
                              [class.param-changed]="this.isChanged('endStrike')"
                              [(strike)]="this.endStrike" [showClearButton]="true"
                              [disable]="this.strikePairDisabled"
                              (strikeChanged)="this.emit()"></ets-strikes-dropdown>
      </div>
    </div>

    <!-- "Adjust to Expected Range" — the RANGE form's second way to name its extent (owner,
         vrequest 2026-08-19): instead of typing two strikes, take them from the option-implied
         expected move, which the backend measures. Ticking it disables and CLEARS the pair above:
         "you either pick a range by yourself or you adjust to a dynamic range".

         ⚠ RANGE ONLY (owner, 2026-08-20: "we now don't think about fill gap and the other modes").
         It sits below the pair rather than inside it because the pair block is SHARED with Fill
         Gap, which does not offer this — see showExpectedRange and strikePairDisabled, both of
         which name the form rather than the shared block.

         ⚠ DISABLED IN PLACE when Expected Move cannot produce a band (its enable box is off in App
         Settings, or Custom Percentage carries no percent). His ruling, and the standing house
         preference: the setting lives in another dialog, so a control that vanished would leave no
         way to discover that the option exists at all. -->
    <dx-check-box *ngIf="this.showExpectedRange" text="Adjust to Expected Range"
                  [class.param-changed]="this.isChanged('useExpectedRange')"
                  [value]="this.useExpectedRange" [disabled]="this.expectedRangeBoxLocked"
                  (onValueChanged)="this.onExpectedRange($event)"></dx-check-box>

    <!-- The cause lives in a DIFFERENT dialog, so a greyed box with no explanation reads as broken.
         OPG says the same thing in a tooltip; this says it in the panel's own .hint idiom, which the
         Adjust tab already uses under its date chips.

         ONE message, not two, because there is only one disabled state: the box releases its tick
         the moment it goes disabled (owner, 2026-08-20), so "disabled" always means "disabled and
         unchecked, strikes yours to pick". -->
    <div class="hint" *ngIf="this.showExpectedRange && !this.expectedRangeAvailable">
      Turn Expected Move on in Application Settings to Adjust to it.
    </div>

    <!-- THE TWO-SIDED FOLLOW-THROUGH (CR-20260824-015 - the form REWRITTEN, per the approved
         mockup): two independent side blocks, each with its own enable, start strike, Harvest and
         minimum (mode + amount); then ONE global Close Remaining Positions toggle with its hint
         line. The Up|Down direction set is gone with the old form. A disabled side's controls
         stay MOUNTED and disabled in place (the standing house preference - a vanished control
         leaves no way to discover it), keeping whatever was typed for re-enabling. -->
    <div class="ft-sides" *ngIf="this.isFollowThrough">
      <div class="ft-side" [class.is-disabled]="!this.ftUpEnabled">
        <dx-check-box text="Up" [value]="this.ftUpEnabled"
                      [class.param-changed]="this.isChanged('ft.up.enabled')"
                      (onValueChanged)="this.onFtUpEnabled($event)"></dx-check-box>
        <div class="field">
          <div class="label">Start Strike</div>
          <ets-strikes-dropdown [targetInstrument]="this.targetInstrument" [expiration]="this.expiration"
                                [class.param-changed]="this.isChanged('ft.up.startStrike')"
                                [(strike)]="this.ftUpStrike" [showClearButton]="true"
                                [disable]="!this.ftUpEnabled"
                                (strikeChanged)="this.emit()"></ets-strikes-dropdown>
        </div>
        <dx-check-box text="Harvest Peaks" [(value)]="this.ftUpHarvest"
                      [class.param-changed]="this.isChanged('ft.up.harvest')"
                      [disabled]="!this.ftUpEnabled"
                      (onValueChanged)="this.emit()"></dx-check-box>
        <dx-button-group
          [class.param-changed]="this.isChanged('ft.up.minProfitMode')"
          [items]="this.minProfitModeButtons"
          keyExpr="id"
          selectionMode="single"
          stylingMode="contained"
          [selectedItemKeys]="[this.ftUpMinProfitMode]"
          [disabled]="!this.ftUpEnabled"
          (onItemClick)="this.onFtUpMinProfitMode($event)"
        ></dx-button-group>
        <dx-number-box [(value)]="this.ftUpMinProfit" placeholder="Minimum Profit Target"
                       [class.param-changed]="this.isChanged('ft.up.minProfit')"
                       [showClearButton]="true" format="$#,##0.00" [showSpinButtons]="true" [min]="0"
                       [disabled]="!this.ftUpEnabled"
                       (onValueChanged)="this.emit()"></dx-number-box>
      </div>

      <div class="ft-side" [class.is-disabled]="!this.ftDownEnabled">
        <dx-check-box text="Down" [value]="this.ftDownEnabled"
                      [class.param-changed]="this.isChanged('ft.down.enabled')"
                      (onValueChanged)="this.onFtDownEnabled($event)"></dx-check-box>
        <div class="field">
          <div class="label">Start Strike</div>
          <ets-strikes-dropdown [targetInstrument]="this.targetInstrument" [expiration]="this.expiration"
                                [class.param-changed]="this.isChanged('ft.down.startStrike')"
                                [(strike)]="this.ftDownStrike" [showClearButton]="true"
                                [disable]="!this.ftDownEnabled"
                                (strikeChanged)="this.emit()"></ets-strikes-dropdown>
        </div>
        <dx-check-box text="Harvest Peaks" [(value)]="this.ftDownHarvest"
                      [class.param-changed]="this.isChanged('ft.down.harvest')"
                      [disabled]="!this.ftDownEnabled"
                      (onValueChanged)="this.emit()"></dx-check-box>
        <dx-button-group
          [class.param-changed]="this.isChanged('ft.down.minProfitMode')"
          [items]="this.minProfitModeButtons"
          keyExpr="id"
          selectionMode="single"
          stylingMode="contained"
          [selectedItemKeys]="[this.ftDownMinProfitMode]"
          [disabled]="!this.ftDownEnabled"
          (onItemClick)="this.onFtDownMinProfitMode($event)"
        ></dx-button-group>
        <dx-number-box [(value)]="this.ftDownMinProfit" placeholder="Minimum Profit Target"
                       [class.param-changed]="this.isChanged('ft.down.minProfit')"
                       [showClearButton]="true" format="$#,##0.00" [showSpinButtons]="true" [min]="0"
                       [disabled]="!this.ftDownEnabled"
                       (onValueChanged)="this.emit()"></dx-number-box>
      </div>
    </div>

    <!-- ONE toggle for both sides (the owner replaced the sketched per-side "Clear Opposite
         Side" boxes with it): always live on this form, no availability gating - when the sides
         meet, the remaining region is empty and the toggle simply has nothing to act on. The
         hint line under it says what "remaining" resolves to right now (approved on the mockup),
         in the panel's own .hint idiom. -->
    <ng-container *ngIf="this.isFollowThrough">
      <dx-check-box text="Close Remaining Positions" [(value)]="this.ftCloseRemaining"
                    [class.param-changed]="this.isChanged('ft.closeRemaining')"
                    (onValueChanged)="this.emit()"></dx-check-box>
      <div class="hint">{{ this.ftRemainingHint }}</div>
    </ng-container>

    <!-- Harvest Peaks — lives INSIDE Price Range (owner, vrequest 2026-07-27) because it decides what
         happens to the line inside the extent the form just defined. Shown for every form EXCEPT the
         two-sided Follow-Through (CR-20260824-015), which carries the command PER SIDE in its own
         blocks above - one global answer to two per-side questions would be ambiguous, not gated. -->
    <dx-check-box *ngIf="this.allowHarvest && !this.isFollowThrough" text="Harvest Peaks" [(value)]="this.harvest"
                  [class.param-changed]="this.isChanged('harvest')"
                  (onValueChanged)="this.emit()"></dx-check-box>

  </fieldset>

  <!-- No engine/strictness/route checkboxes belong here (retired 2026-08-08: one composer set,
       clean delivery only). Do not reintroduce one - see hg-copilot-risk-inputs.component.ts. -->

  <!-- Minimum Profit — the input MODE is a two-button selector (vrequest 2026-07-20). It REPLACED
       a "Normalized" checkbox outright: the modes are mutually exclusive and decide what the SAME
       typed number means, which a checkbox stated only on its checked side. Normalized is now the
       DEFAULT. The $ format is kept in BOTH modes (owner ruling: a normalized value is still a
       dollar amount, just per unit). Selector then input on its own row — no .inline wrapper
       needed: the fieldset is already a flex column and the button group is full-width by the
       shared .dx-buttongroup-wrapper rule. -->
  <!-- HIDDEN ON THE TWO-SIDED FOLLOW-THROUGH (CR-20260824-015): each side carries its OWN
       minimum inside the Price Range fieldset, so this global figure would be a second answer to
       a question the form no longer asks - and a hidden control's value never travels (buildState
       reports 0 there, which the backend does not read on this form). -->
  <fieldset class="fieldset" *ngIf="!this.isFollowThrough">
    <legend class="fieldset-title">Minimum Profit</legend>

    <dx-button-group
      [class.param-changed]="this.isChanged('minProfit.mode')"
      [items]="this.minProfitModeButtons"
      keyExpr="id"
      selectionMode="single"
      stylingMode="contained"
      [selectedItemKeys]="[this.minProfitMode]"
      (onItemClick)="this.onMinProfitMode($event)"
    ></dx-button-group>

    <dx-number-box [(value)]="this.minProfit" placeholder="Minimum Profit Target" [showClearButton]="true"
                   [class.param-changed]="this.isChanged('minProfit.amount')"
                   format="$#,##0.00" [showSpinButtons]="true" [min]="0"
                   (onValueChanged)="this.emit()"></dx-number-box>
  </fieldset>

  <!-- THE WHOLE MAX RISK SECTION IS RANGE-ONLY (owner, vrequest 2026-08-18): "max risk section
       should only be visible in the range mode. If we have follow through, wall, we don't see it
       there." Legend, mode toggle, levels, risk side and half risk go together — showMaxRisk is the
       one condition, so the section cannot come apart form by form.

       ⚠ This REPLACES the narrower 2026-08-17 reading that hid only the relative levels and left the
       Dollar arm on every form. Same question, same illusion; half of it hidden is not half an
       illusion. Do not restore a per-arm rule here.

       ⚠ Hidden, NOT disabled — his ruling, against the standing preference, because a dead-but-
       visible control IS the illusion. See buildMaxRisk for what travels while it is hidden. -->
  <fieldset class="fieldset" *ngIf="this.showMaxRisk">
    <legend class="fieldset-title">Max Risk</legend>

    <dx-button-group
      [class.param-changed]="this.isChanged('maxRisk.kind')"
      [items]="this.maxRiskButtons"
      keyExpr="id"
      selectionMode="single"
      stylingMode="contained"
      [selectedItemKeys]="[this.maxRiskKind]"
      (onItemClick)="this.onMaxRiskKind($event)"
    ></dx-button-group>

    <div class="field" *ngIf="this.maxRiskKind === 'dollar'">
      <div class="label">Maximum Risk in Dollars</div>
      <div class="inline">
        <dx-number-box class="grow"
                       [class.param-changed]="this.isChanged('maxRisk.amount')"
                       [(value)]="this.dollarAmount" [min]="0" [showClearButton]="true"
                       format="$#,##0.00" [showSpinButtons]="true"
                       (onValueChanged)="this.emit()"></dx-number-box>
        <!-- Stays a CHECKBOX while Minimum Profit above became a two-button selector. The
             asymmetry is DELIBERATE (owner ruling, vrequest 2026-07-20), not drift: a matching
             "Normalized | Raw ($)" pair here would put "Raw ($)" one row beneath the
             "Dollar ($)" selector above — two unrelated meanings of $ stacked together.
             Default is CHECKED, so both inputs still agree on Normalized.
             Do NOT "harmonize" this control with the one above. -->
        <dx-check-box text="Normalized" [class.param-changed]="this.isChanged('maxRisk.normalized')"
                      [(value)]="this.dollarNormalized" (onValueChanged)="this.onDollarNormalizedEdited()"></dx-check-box>
      </div>
    </div>

    <!-- THE DOLLAR ARM'S OWN LEVELS + RISK SIDE (CR-20260819-002 R-2/R-4): the level dictates the
         PLACEMENT of the risk, so the Dollar tab carries [Moderate | Aggressive (| All)] — no Low
         ("low assumes no risk"; a $0 amount is the dollar spelling of no-risk) — and the same Risk
         Side set the relative arm offers, reading the SAME field so the choice survives switching
         arms. NO ½ Half Risk here (R-3: "if you want half, just go and enter half") and NO
         watermark — the watermark is the relative arm's Low state, which the dollar arm cannot
         be in. -->
    <ng-container *ngIf="this.showDollarLevels">
      <dx-button-group
        [class.param-changed]="this.isChanged('maxRisk.dollarLevel')"
        [items]="this.dollarLevelButtons"
        keyExpr="id"
        selectionMode="single"
        stylingMode="contained"
        [selectedItemKeys]="[this.dollarLevel]"
        [selectedItems]="this.dollarLevelSelectedItems"
        (onItemClick)="this.onDollarLevel($event)"
      ></dx-button-group>

      <div class="field">
        <div class="label">Risk Side</div>
        <dx-button-group
          [class.param-changed]="this.isChanged('riskSide')"
          [items]="this.riskSideButtons"
          keyExpr="id"
          selectionMode="single"
          stylingMode="contained"
          [selectedItemKeys]="[this.riskSide]"
          (onItemClick)="this.onRiskSide($event)"
        ></dx-button-group>
      </div>
    </ng-container>

    <dx-button-group
      *ngIf="this.showRelativeLevels"
      [class.param-changed]="this.isChanged('maxRisk.level')"
      [items]="this.relativeLevelButtons"
      keyExpr="id"
      selectionMode="single"
      stylingMode="contained"
      [selectedItemKeys]="[this.relativeLevel]"
      [selectedItems]="this.relativeLevelSelectedItems"
      (onItemClick)="this.onRelativeLevel($event)"
    ></dx-button-group>

    <!-- RISK SIDE + HALF RISK, RELATIVE ARM (owner, vrequest 2026-08-18; amended by
         CR-20260819-002 R-9). The RISK SIDE belongs to BOTH arms now — the Dollar arm renders its
         own copy of the set above, on the same field — so only ½ HALF RISK still rides exclusively
         with the relative levels (R-3: on a typed amount, "if you want half, just go and enter
         half"). This block is the RELATIVE arm's rendering: side + half + the Low watermark. A
         labelled row + segmented set, the same shape as "Start / End Strike" above, rather than a
         nested fieldset — a legend inside a legend reads heavy in this narrow column. -->
    <!-- LOW GETS A WATERMARK INSTEAD OF THE CONTROLS (owner, vrequest 2026-08-19) — "the only level
         that contains no risk is low, which means this panel makes no sense when low is selected".
         The two controls stay in the DOM and lose only their VISIBILITY, because he asked for the
         space to be kept: "hide it visually, so don't lose its space, so I don't want the window to
         jump up and down". That is why this is a wrapper with an overlay and not a third *ngIf —
         an *ngIf would take the height with it, and any min-height we typed instead would be a
         number to keep in step with two controls it cannot see. -->
    <div class="risk-side-block" *ngIf="this.showRelativeLevels"
         [class.is-muted]="!this.showRiskSideControls">

      <div class="field">
        <div class="label">Risk Side</div>
        <dx-button-group
          [class.param-changed]="this.isChanged('riskSide')"
          [items]="this.riskSideButtons"
          keyExpr="id"
          selectionMode="single"
          stylingMode="contained"
          [selectedItemKeys]="[this.riskSide]"
          (onItemClick)="this.onRiskSide($event)"
        ></dx-button-group>
      </div>

      <!-- "½ Half Risk" — his own label, drawn on the whiteboard as the fraction followed by the
           words. Full risk (unchecked) is the 1:1 ceiling: the risk taken equals the minimum profit
           requested. Checked halves it. -->
      <dx-check-box text="½ Half Risk" [(value)]="this.halfRisk"
                    [class.param-changed]="this.isChanged('halfRisk')"
                    (onValueChanged)="this.emit()"></dx-check-box>

      <!-- The watermark. Fills the block the two controls above still occupy and centres on it, so
           the line sits on the middle of the space they left (his instruction: "center the text
           vertically relative to the size of hidden items"). No border — he dropped the box he drew. -->
      <div class="risk-side-watermark" *ngIf="!this.showRiskSideControls">
        No Risk Side Options Available For "Low" Risk Mode
      </div>

    </div>
  </fieldset>

</div>
